import React from "react";
import { Smartphone, MessageSquare, Mail } from "lucide-react";

const SYNERGIES = [
  {
    icon: Smartphone,
    id: "mobile-money",
    partner: "Mobile Money",
    with: "Orange Money & MTN",
    body: "Vos locataires payent directement depuis leur téléphone via Orange Money ou MTN Mobile Money. Paiements sécurisés, confirmations instantanées, réconciliation automatique dans Mikassa Agency.",
    tag: "Paiement Mobile",
  },
  {
    icon: MessageSquare,
    id: "sms",
    partner: "SMS",
    with: "Notifications SMS automatiques",
    body: "Rappels de loyer 5 jours avant l'échéance, notifications de retard à 1, 5 et 10 jours. Réduisez les impayés sans effort grâce à des rappels automatiques et professionnels.",
    tag: "Communication SMS",
  },
  {
    icon: Mail,
    id: "email",
    partner: "Email & Documents",
    with: "Quittances & contrats automatiques",
    body: "Génération automatique des quittances de loyer, envoi par email aux locataires, archivage numérique. États des lieux avec signature électronique et PDF automatique.",
    tag: "Documents & Email",
  },
];

const Synergies = () => {
  return (
    <section
      id="integrations"
      data-testid="synergies-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        {/* <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Intégrations puissantes
          </span>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-20">
          <h2
            data-testid="synergies-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Connecté aux outils<br />
            <span className="text-[#E8611A]">que vous utilisez</span> déjà.
          </h2>
          {/* <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Mobile Money, SMS, Email — Mikassa Agency s'intègre aux services
            ivoiriens pour automatiser votre gestion locative de bout en bout.
          </p> */}
        </div>

        {/* Nodal diagram + cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* SVG nodal diagram */}
          <div className="lg:col-span-5 relative aspect-square max-w-md mx-auto w-full">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#E8611A" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#0C2340" stopOpacity="0.2" />
                </linearGradient>
              </defs>
              {/* Diamond frame */}
              <path
                d="M100 20 L180 100 L100 180 L20 100 Z"
                stroke="#0C2340"
                strokeOpacity="0.15"
                strokeWidth="1"
                fill="none"
              />
              <path
                d="M100 40 L160 100 L100 160 L40 100 Z"
                stroke="#0C2340"
                strokeOpacity="0.08"
                strokeWidth="1"
                fill="none"
              />
              {/* Connectors */}
              <line x1="100" y1="100" x2="100" y2="30" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3s" repeatCount="indefinite" />
              </line>
              <line x1="100" y1="100" x2="170" y2="130" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3.4s" repeatCount="indefinite" />
              </line>
              <line x1="100" y1="100" x2="30" y2="130" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3.8s" repeatCount="indefinite" />
              </line>
              {/* Central node = Mikassa */}
              <rect x="86" y="86" width="28" height="28" transform="rotate(45 100 100)" fill="#0C2340" />
              <text
                x="100"
                y="104"
                textAnchor="middle"
                fontFamily="Montserrat"
                fontWeight="900"
                fontSize="8"
                fill="#FAFAF8"
              >
                Mikassa
              </text>
              {/* Outer nodes */}
              {[
                { x: 100, y: 30, label: "Mobile $" },
                { x: 170, y: 130, label: "SMS" },
                { x: 30, y: 130, label: "Email" },
              ].map((n) => (
                <g key={n.label}>
                  <rect
                    x={n.x - 10}
                    y={n.y - 10}
                    width="20"
                    height="20"
                    transform={`rotate(45 ${n.x} ${n.y})`}
                    fill="#E8611A"
                  />
                  <text
                    x={n.x}
                    y={n.y + 3}
                    textAnchor="middle"
                    fontFamily="Montserrat"
                    fontWeight="700"
                    fontSize="6"
                    fill="#FAFAF8"
                  >
                    {n.label}
                  </text>
                </g>
              ))}
            </svg>
            <div className="mt-4 text-center font-body text-[0.65rem] uppercase tracking-[0.28em] text-[#636E72]">
              Mikassa Agency · Intégré à l'écosystème ivoirien
            </div>
          </div>

          {/* Cards column */}
          <div className="lg:col-span-7 space-y-4">
            {SYNERGIES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  data-testid={`synergy-${s.id}`}
                  className="group flex items-start gap-6 border-t border-[#0C2340]/12 pt-6 pb-2 hover:border-[#E8611A] transition-colors duration-500"
                >
                  <div className="shrink-0 w-14 h-14 flex items-center justify-center bg-[#0C2340] group-hover:bg-[#E8611A] transition-colors">
                    <Icon size={22} strokeWidth={1.4} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-heading font-black text-[#0C2340]/25 text-2xl tracking-tighter">
                        0{i + 1}
                      </span>
                      <span className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-[#E8611A]">
                        {s.tag}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-[#0C2340] text-xl md:text-2xl tracking-tight mb-2">
                      {s.with}
                    </h3>
                    <p className="font-body text-[#2D3436] text-sm md:text-base leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Synergies;
